import type { SeriesBarChartConfig, SeriesChartDataItem } from './types';
import { cornerRadiusFor } from '../shared/barRadius';

// Matches amcharts4: columnTemplate.width = 54 (fixed pixel width).
const BAR_WIDTH = 54;

type LabelParam = { dataIndex?: number };

export const buildSeriesBarSeries = (data: SeriesChartDataItem[], config: SeriesBarChartConfig) => {
  const { theme, mainColor, fadedColor, average, formatAmount, privacyMask } = config;

  const predictedData = data.map((d) => ({
    value: d.totalAmount,
    itemStyle: { color: fadedColor, borderRadius: cornerRadiusFor(d.totalAmount) },
  }));

  // Value label turns white only when its posted bar is tall enough to sit behind it
  // (amcharts4: column.maxHeight < 20 ? dark : white). Approximate "tall enough" as a
  // fraction of the chart's max value since pixel height isn't known at build time.
  const maxAmount = Math.max(0, ...data.flatMap((d) => [Math.abs(d.totalAmount), Math.abs(d.currentAmount)]));
  const labelOnBarThreshold = maxAmount * 0.12;

  const postedData = data.map((d) => {
    const labelOnBar = maxAmount > 0 && Math.abs(d.currentAmount) >= labelOnBarThreshold;
    return {
      value: d.currentAmount,
      itemStyle: { color: mainColor, borderRadius: cornerRadiusFor(d.currentAmount) },
      label: { color: labelOnBar ? theme.bgColor : theme.textSecondaryColor },
    };
  });

  const averageFormatted = String(privacyMask(formatAmount(average, 'USD', '0,0')));
  const { averageLineColor } = theme;

  // Average annotation + zero baseline, drawn once via the predicted series.
  const markLine = {
    silent: true,
    symbol: ['none', 'none'] as [string, string],
    data: [
      {
        // Zero baseline (amcharts4 drew an axisRange at value 0 in the faded color).
        yAxis: 0,
        lineStyle: { color: fadedColor, width: 1, type: 'solid' as const, opacity: 1 },
        label: { show: false },
      },
      {
        yAxis: average,
        lineStyle: { color: averageLineColor, width: 2, type: 'solid' as const, opacity: 1 },
        label: {
          show: true,
          // 'start' anchors the label in the left margin (grid.left), so the line begins to
          // its right — matching amcharts4 where the pill sits beside the plot (valueAxis.width).
          position: 'start' as const,
          // distance 0 + right-aligned pill puts the pill's right edge exactly at the line's
          // start, so there's no gap between the pill and the line.
          distance: 0,
          // 'start' centers the whole block on the line. "Average" above + pill + an equal
          // transparent spacer below makes the pill the block's vertical center, so the line
          // runs through the middle of the pill (matches amcharts4).
          formatter: `{title|Average}\n{pill|${averageFormatted}}\n{spacer| }`,
          rich: {
            title: {
              color: theme.textSecondaryColor,
              fontFamily: theme.fontFamily,
              fontSize: 14,
              lineHeight: 20,
              align: 'left' as const,
            },
            pill: {
              color: theme.bgColor,
              backgroundColor: averageLineColor,
              fontFamily: theme.fontFamily,
              fontSize: 14,
              lineHeight: 18,
              padding: [4, 12],
              borderRadius: 4,
              align: 'right' as const,
            },
            spacer: {
              color: 'transparent',
              fontSize: 1,
              lineHeight: 20,
            },
          },
        },
      },
    ],
  };

  // Value labels show the full (total) amount and sit just above the baseline — inside the
  // bar near its bottom when one is present (white), or at the baseline otherwise (dark).
  // Matches amcharts4's LabelBullet on series2 (locationY: 1 with the dy/fill adapters).
  const label = {
    show: true,
    position: 'insideBottom' as const,
    offset: [0, -10] as [number, number],
    fontFamily: theme.fontFamily,
    fontSize: 12,
    formatter: (p: LabelParam) =>
      String(privacyMask(formatAmount(data[p.dataIndex ?? 0]?.totalAmount ?? 0, 'USD', '0,0'))),
  };

  return [
    {
      name: 'Predicted',
      type: 'bar' as const,
      barWidth: BAR_WIDTH,
      barGap: '-100%',
      z: 1,
      data: predictedData,
      emphasis: { disabled: true as const },
      markLine,
    },
    {
      name: 'Posted',
      type: 'bar' as const,
      barWidth: BAR_WIDTH,
      barGap: '-100%',
      z: 2,
      data: postedData,
      emphasis: { disabled: true as const },
      label,
    },
  ];
};
